import React, { memo, useMemo, useState, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIsFetching } from '@tanstack/react-query';
import { Button, ComponentSize, Tooltip } from '@tcc/fds';
import { getMessage } from 'util/locale';
import { FormData } from '../../types';
import { SEED_DENSITY_RECOMMENDATION_QUERY_KEY } from '../../constants';

interface GenerateAllButtonProps {
  onGenerate: () => void;
}

export const GenerateAllButton = memo(function GenerateAllButton({ onGenerate }: GenerateAllButtonProps) {
  const { control } = useFormContext<FormData>();

  const watchedFields = useWatch({ control, name: 'fields' });
  const watchedGrainPrice = useWatch({ control, name: 'grainPrice' });
  const isFetchingRecommendations = useIsFetching({ queryKey: [SEED_DENSITY_RECOMMENDATION_QUERY_KEY] });

  const [debouncedIsFetching, setDebouncedIsFetching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const isFetching = isFetchingRecommendations > 0;

    if (isFetching) {
      clearTimeout(timerRef.current);
      setDebouncedIsFetching(true);
    } else {
      timerRef.current = setTimeout(() => setDebouncedIsFetching(false), 100);
    }

    return () => clearTimeout(timerRef.current);
  }, [isFetchingRecommendations]);

  const allHaveRecommendations = useMemo(
    () => watchedFields?.length > 0 && watchedFields.every(f => !!f?.recommendation),
    [watchedFields]
  );
  const allInputsFilled = useMemo(
    () => !!watchedGrainPrice && watchedFields?.length > 0 && watchedFields.every(
      f => !!f?.hybrid && !!f?.avgFixedPopulation && !!f?.seedCostPerBag && !!f?.targetYield
    ),
    [watchedFields, watchedGrainPrice]
  );
  const disabled = useMemo(
    () => !allInputsFilled || allHaveRecommendations || debouncedIsFetching,
    [allInputsFilled, allHaveRecommendations, debouncedIsFetching]
  );

  const tooltipMessage = useMemo(() => {
    if (!watchedGrainPrice) return getMessage('bulk-fvss-script-generation-generate-all-tooltip-grain-price-required');
    if (debouncedIsFetching) return getMessage('bulk-fvss-script-generation-generate-all-tooltip-loading');
    if (allHaveRecommendations) return getMessage('bulk-fvss-script-generation-generate-all-tooltip-all-generated');
    if (!allInputsFilled) return getMessage('bulk-fvss-script-generation-generate-all-tooltip-fill-inputs');
    return '';
  }, [debouncedIsFetching, allHaveRecommendations, watchedGrainPrice, allInputsFilled]);

  const button = useMemo(() => (
    <Button
      testId="generate-all-button"
      type="button"
      size={ComponentSize.Md}
      onClick={onGenerate}
      disabled={disabled}
    >
      {getMessage('generate-all')}
    </Button>
  ), [onGenerate, disabled]);

  if (disabled) {
    return (
      <Tooltip testId="generate-all-tooltip" trigger={button}>
        {tooltipMessage}
      </Tooltip>
    );
  }

  return button;
});
